import { FormGroup, FormControl, ValidatorFn, ValidationErrors, AbstractControl, FormArray } from '@angular/forms';
import { AuctionRule } from '../../models/auctionRule';

export const emailValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const emailRegexp = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,63}$/;
  if (control.value && !emailRegexp.test(control.value)) {
    return { invalidEmail: true };
  }
  return null;
};

export const httpsUrlValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const httpsUrlRegexp = /^https:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;
    if (control.value && !httpsUrlRegexp.test(control.value)) {
        return { invalidHttpsUrl: true };
    }
    return null;
}

export const matchingPasswords: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const passwordControl = control.get('password');
  const confirmControl = control.get('confirmPassword');

  if (!passwordControl || !confirmControl) {
    return null;
  }

  return passwordControl.value === confirmControl.value
    ? null
    : { mismatchedPasswords: true };
};

export function minimumBid(bidKey: string, minBid: number): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const bid = group.get(bidKey);
    if (!bid) {
      return null;
    }

    const rawValue = typeof bid.value === 'string' ? bid.value : String(bid.value ?? '');
    const bidNumber = +rawValue.replace(/[^\d.]/g, ''); // strip non-numeric chars, then coerce to number

    const remainingErrors = { ...bid.errors };
    delete remainingErrors['minimumBid'];

    if (isNaN(bidNumber) || bidNumber < minBid) {
      bid.setErrors({ ...remainingErrors, minimumBid: true });
      return { minimumBid: true };
    }

    bid.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
    return null;
  };
}

export interface BooleanFn {
    (): boolean;
}

export function conditionalValidator(predicate: BooleanFn, validator: ValidatorFn, errorNamespace?: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent) {
      return null;
    }

    let error: ValidationErrors | null = null;
    if (predicate()) {
      error = validator(control);
    }

    return errorNamespace && error ? { [errorNamespace]: error } : error;
  };
}

export const yearBuiltValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) {
    return null;
  }

  const currentYear = new Date().getFullYear();
  const yearBuilt = +control.value;
  const diff = currentYear - yearBuilt;

  if (isNaN(yearBuilt) || diff < 0 || yearBuilt < 1500) {
    return { invalidYearBuilt: true };
  }

  return null;
};

const SCHEDULE_ERROR_KEYS = ['badSchedule', 'scheduleTooLong', 'postponeTooLong'];

function setScheduleError(row: AbstractControl, field: string, errorKey: string | null): void {
  const fieldControl = row.get(field);
  if (!fieldControl) {
    return;
  }
  const remainingErrors = { ...fieldControl.errors };
  SCHEDULE_ERROR_KEYS.forEach(key => delete remainingErrors[key]);
  if (errorKey) {
    remainingErrors[errorKey] = true;
  }
  fieldControl.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
}

export function auctionScheduleValidator(ar: AuctionRule): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const schedule = control.get('schedule') as FormArray | null;
    if (!schedule || schedule.length === 0) {
      return null;
    }

    let hasBadSchedule = false;

    // Reset transient schedule errors from a previous run before recomputing.
    schedule.controls.forEach(row => {
      ['auctionStarting', 'auctionEnding', 'autoRenewTimes', 'autoRenewDays'].forEach(field => {
        setScheduleError(row, field, null);
      });
    });

    // Check start/end ordering, and overlap with the previous row.
    let previousRow: AbstractControl | undefined;
    schedule.controls.forEach(row => {
      const start = row.value.auctionStarting ? new Date(row.value.auctionStarting) : null;
      const end = row.value.auctionEnding ? new Date(row.value.auctionEnding) : null;

      if (start && end && start.getTime() >= end.getTime()) {
        setScheduleError(row, 'auctionStarting', 'badSchedule');
        setScheduleError(row, 'auctionEnding', 'badSchedule');
        hasBadSchedule = true;
      }

      if (previousRow && start) {
        const previousEnd = previousRow.value.auctionEnding ? new Date(previousRow.value.auctionEnding) : null;
        if (previousEnd && previousEnd.getTime() > start.getTime()) {
          setScheduleError(row, 'auctionStarting', 'badSchedule');
          setScheduleError(previousRow, 'auctionEnding', 'badSchedule');
          hasBadSchedule = true;
        }
      }

      previousRow = row;
    });

    // Check each auction's duration against the rule's max.
    if (ar?.maxAuctionDays) {
      schedule.controls.forEach(row => {
        const start = row.value.auctionStarting ? new Date(row.value.auctionStarting) : null;
        const end = row.value.auctionEnding ? new Date(row.value.auctionEnding) : null;
        if (!start || !end) {
          return;
        }

        const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 3600 * 24));
        if (diffDays < 0) {
          setScheduleError(row, 'auctionStarting', 'badSchedule');
          setScheduleError(row, 'auctionEnding', 'badSchedule');
          hasBadSchedule = true;
        } else if (diffDays > ar.maxAuctionDays!) {
          setScheduleError(row, 'auctionStarting', 'scheduleTooLong');
          setScheduleError(row, 'auctionEnding', 'scheduleTooLong');
          hasBadSchedule = true;
        }
      });
    }

    // Check the last auction's allowed postponement window. Only the final
    // row can auto-renew/postpone; it's capped relative to the previous
    // row's end date (or its own start date, if it's the only row).
    if ((ar?.maxLastAuctionPostponeDays ?? 0) > 0) {
      const lastRow = schedule.controls[schedule.length - 1];
      const { autoRenewTimes, autoRenewDays, auctionStarting, auctionEnding } = lastRow.value;

      if (autoRenewTimes && autoRenewDays && auctionStarting && auctionEnding) {
        const previousRowEnd =
          schedule.length > 1 ? schedule.controls[schedule.length - 2].value.auctionEnding : null;
        const referenceDate = previousRowEnd ? new Date(previousRowEnd) : new Date(auctionStarting);

        const projectedEnd = new Date(auctionEnding).getTime() + autoRenewTimes * autoRenewDays * (1000 * 3600 * 24);
        const latestAllowedEnd = referenceDate.getTime() + ar.maxLastAuctionPostponeDays! * (1000 * 3600 * 24);

        if (projectedEnd > latestAllowedEnd) {
          setScheduleError(lastRow, 'autoRenewTimes', 'postponeTooLong');
          setScheduleError(lastRow, 'autoRenewDays', 'postponeTooLong');
        }
      }
    }

    return hasBadSchedule ? { badSchedule: true } : null;
  };
}

export function isGuid(value: string): boolean {
  const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return guidRegex.test(value);
}